import axios, { AxiosError } from 'axios';
import { getCustomerToken } from './customer-auth';

// Public gift card endpoints don't need auth (guest checkout is the
// happy path), so we use a bare axios instance instead of routing
// through the customer-api auth interceptors. If the buyer happens to
// be signed in, we forward their token so the row gets attributed to
// their user_id — but it's not required.
const BASE_URL =
  (import.meta as any).env?.VITE_API_BASE_URL || 'http://localhost:3002';

const giftCardApi = axios.create({
  baseURL: BASE_URL,
  timeout: 30000,
});

giftCardApi.interceptors.request.use((config) => {
  const token = getCustomerToken();
  if (token) {
    config.headers = config.headers ?? {};
    (config.headers as any).Authorization = `Bearer ${token}`;
  }
  return config;
});

export function giftCardApiErrorMessage(
  err: unknown,
  fallback = 'Something went wrong.',
): string {
  const e = err as AxiosError<any>;
  return e?.response?.data?.message || (e as any)?.message || fallback;
}

export type GiftCardPurchasePayload = {
  amount: number;
  currency?: string;
  buyer_first_name: string;
  buyer_last_name: string;
  buyer_email: string;
  recipient_first_name: string;
  recipient_last_name: string;
  recipient_email: string;
  message?: string | null;
  occasion?: string | null;
  delivery_method?: 'RECIPIENT' | 'SELF';
  send_date?: string | null;
};

export type GiftCardPurchaseResponse = {
  status: boolean;
  id: string;
  // The Stripe-hosted Checkout URL the website redirects to. The card
  // is created as PENDING_PAYMENT; it flips to PAID after Stripe
  // confirms (via webhook or the post-redirect confirm endpoint).
  checkout_url: string;
  stripe_session_id: string;
  amount: number;
  currency: string;
  recipient_email: string;
  message: string;
};

export type GiftCardConfig = {
  min_amount: number;
  max_amount: number;
  currency: string;
  preset_amounts: number[];
};

/**
 * Public "Choose Amount" config — admin-configured min/max and the
 * preset denomination tiles. Drives the gift-card amount step so the
 * values aren't hardcoded in the UI.
 */
export async function getGiftCardConfig() {
  const res = await giftCardApi.get('/gift-card/config');
  return res.data as { status: boolean; info: GiftCardConfig; message: string };
}

export async function purchaseGiftCard(payload: GiftCardPurchasePayload) {
  const res = await giftCardApi.post('/gift-card/purchase', payload);
  return res.data as GiftCardPurchaseResponse;
}

export type GiftCardCheckoutConfirm = {
  status: boolean;
  id: string;
  code: string;
  gift_card_status:
    | 'PENDING_PAYMENT'
    | 'PAID'
    | 'PARTIALLY_REDEEMED'
    | 'REDEEMED'
    | 'EXPIRED'
    | 'FAILED'
    | 'CANCELLED';
  payment_status: 'paid' | 'unpaid' | 'no_payment_required' | string;
  amount: number;
  currency: string;
  recipient_email: string;
  recipient_first_name: string | null;
};

/**
 * Called on /gift-card/sent after Stripe redirects back. The session
 * id is in the URL ?session_id=... — we ask the backend to confirm
 * the session against Stripe + render the resulting card.
 */
export async function confirmGiftCardCheckout(sessionId: string) {
  const res = await giftCardApi.get(
    `/gift-card/checkout/${encodeURIComponent(sessionId)}`,
  );
  return res.data as GiftCardCheckoutConfirm;
}

export async function previewGiftCardByCode(code: string) {
  const res = await giftCardApi.get(`/gift-card/code/${encodeURIComponent(code)}`);
  return res.data as {
    status: boolean;
    info: {
      id: string;
      code: string;
      balance: number;
      currency: string;
      amount_initial: number;
      expires_at: string | null;
    };
  };
}

export type MyGiftCard = {
  id: string;
  code: string;
  amount_initial: number;
  balance: number;
  currency: string;
  status: string;
  message: string | null;
  occasion: string | null;
  sent_at: string | null;
  expires_at: string | null;
  buyer_first_name: string | null;
  buyer_last_name: string | null;
};

export async function listMyReceivedGiftCards() {
  const res = await giftCardApi.get('/gift-card/me');
  return res.data as { status: boolean; data: MyGiftCard[]; message: string };
}
